import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { NavLink, useParams } from "react-router";
import t from "@/lang/lang";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { useGfsCalculationGetQuery } from "@/services/backend/gfs/calculation/api";
import { useGfsOfferEditorContext } from "@/services/backend/gfs/offer/components/offer-editor-context";
import {
  useGfsOfferSetDateMutation,
  useGfsOfferSetValidUntilDateMutation,
} from "@/services/backend/gfs/offer/api";
import {
  GfsOffer,
  SetGfsOfferDateRequest,
  SetGfsOfferValidUntilDateRequest,
} from "@/services/backend/gfs/offer/types";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { formatDate } from "@/shared/lib/utilities/date";
import { DateInput } from "@/shared/components/domain/qpm/inputs/date-input";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  effectiveGfsTargetOrganismSelection,
  GfsOfferSubjectEditor,
  GfsTargetOrganismSelectionEditor,
} from "@/services/backend/gfs/offer/components/target-organism-selection-editor";
import {
  buildGfsOfferSubject,
  buildGfsOfferTitle,
  hasRenderableGfsHtml,
} from "@/services/backend/gfs/offer/lib/target-organism-title";

export function GfsOfferOverviewPage() {
  const { offer, putRequest, updateContent } = useGfsOfferEditorContext();
  const { processId = "" } = useParams();
  const calculation = offer.calculation;
  const content = putRequest.content;
  const selectedCalculationId =
    offer.sourceCalculationId || calculation.sourceId || "";
  const { data: selectedCalculation } = useGfsCalculationGetQuery(
    { id: selectedCalculationId },
    { skip: !selectedCalculationId },
  );
  const targetOrganismSelection = effectiveGfsTargetOrganismSelection(
    offer,
    content.targetOrganismSelection,
  );
  const generatedOfferTitle = buildGfsOfferTitle(
    targetOrganismSelection,
    content.targetOrganisms.length
      ? content.targetOrganisms
      : calculation.targetOrganisms,
  );
  const offerTitle = hasRenderableGfsHtml(content.caseData.measure)
    ? content.caseData.measure
    : generatedOfferTitle;

  return (
    <div className="space-y-4 pb-16">
      <Card>
        <CardHeader>
          <CardTitle>
            {t("Angebot")} -{" "}
            {
              <DocumentNumberById
                documentId={offer.id}
                skip={!offer.immutable}
              />
            }
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <section className="grid gap-4 lg:grid-cols-3">
            <InfoItem
              label={t("Kalkulationsgrundlage")}
              value={
                selectedCalculationId ? (
                  <NavLink
                    className="text-primary underline underline-offset-4"
                    to={`/processes/GFS/${processId}/calculations/${selectedCalculationId}`}
                  >
                    <DocumentNumberById
                      documentId={selectedCalculationId}
                      skip={!selectedCalculation?.immutable}
                    />
                  </NavLink>
                ) : (
                  t("Nicht gewählt")
                )
              }
            />
            <InfoItem
              label={t("Bestandsaufnahme")}
              value={calculation.assessment.subject || t("Ohne Betreff")}
            />
            <InfoItem
              label={t("Snapshot")}
              value={
                calculation.takenAt ? formatDate(calculation.takenAt) : "-"
              }
            />
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <GfsOfferDateSelector offer={offer} />
            <GfsOfferValidUntilDateSelector offer={offer} />
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold">
              {t("Daten für das Angebot")}
            </h2>
            <GfsOfferSubjectEditor
              subject={content.subject}
              disabled={offer.immutable}
              onChange={(subject) =>
                updateContent({ subject, subjectManuallySet: true })
              }
            />
            <GfsTargetOrganismSelectionEditor
              offer={offer}
              value={content.targetOrganismSelection}
              offerTitle={offerTitle}
              disabled={offer.immutable}
              onOfferTitleChange={(measure) =>
                updateContent({
                  caseData: { ...content.caseData, measure },
                })
              }
              onChange={(selection, targetOrganisms) => {
                const nextContent: Partial<typeof content> = {
                  targetOrganismSelection: selection,
                  targetOrganisms,
                  caseData: {
                    ...content.caseData,
                    measure: buildGfsOfferTitle(selection, targetOrganisms),
                  },
                };
                if (!content.subjectManuallySet) {
                  nextContent.subject = buildGfsOfferSubject(
                    selection,
                    targetOrganisms,
                  );
                  nextContent.subjectManuallySet = false;
                }
                updateContent(nextContent);
              }}
            />
          </section>
        </CardContent>
      </Card>
    </div>
  );
}

function GfsOfferDateSelector({ offer }: { offer: GfsOffer }) {
  const [request, setRequest] = useState<SetGfsOfferDateRequest>({
    offerId: offer.id,
    date: offer.date,
  });
  const [setDate, { error, isLoading, isSuccess, reset }] =
    useGfsOfferSetDateMutation();

  const onChangeDate = useCallback((date: string | null) => {
    if (!date) {
      return;
    }
    setRequest((current) => ({ ...current, date }));
  }, []);

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setDate,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
    { toastError: true },
  );

  return (
    <DateInput
      label={t("Angebotsdatum")}
      date={request.date}
      onDateChange={onChangeDate}
      disabled={offer.immutable}
    />
  );
}

function GfsOfferValidUntilDateSelector({ offer }: { offer: GfsOffer }) {
  const [localFieldError, setLocalFieldError] = useState<string | null>(null);
  const [request, setRequest] = useState<SetGfsOfferValidUntilDateRequest>({
    offerId: offer.id,
    validUntilDate: offer.validUntilDate ?? null,
  });
  const [setValidUntilDate, { error, isLoading, isSuccess, reset }] =
    useGfsOfferSetValidUntilDateMutation();

  const onChangeDate = useCallback((validUntilDate: string | null) => {
    const immediateValidationError = validateValidUntilDate(validUntilDate);
    setLocalFieldError(immediateValidationError);
    setRequest((current) => ({ ...current, validUntilDate }));
  }, []);

  const fieldErrors = useMemo(() => {
    if (!localFieldError) {
      return undefined;
    }
    return [localFieldError];
  }, [localFieldError]);

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setValidUntilDate,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
    { toastError: false, skip: localFieldError !== null },
  );

  return (
    <DateInput
      label={t("Gültig bis")}
      date={request.validUntilDate}
      onDateChange={onChangeDate}
      disabled={offer.immutable}
      fieldErrors={fieldErrors}
      resetFieldError={() => setLocalFieldError(null)}
    />
  );
}

function InfoItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-muted-foreground text-sm font-medium">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function validateValidUntilDate(validUntilDate: string | null): string | null {
  if (!validUntilDate) {
    return null;
  }
  const selectedDate = normalizeToUTCDate(new Date(validUntilDate));
  if (Number.isNaN(selectedDate.getTime())) {
    return null;
  }
  if (selectedDate < normalizeToUTCDate(new Date())) {
    return "Das 'Gültig bis'-Datum darf nicht in der Vergangenheit liegen.";
  }
  return null;
}

function normalizeToUTCDate(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
}
